import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Typography
} from "@mui/material";
import { memo, useMemo } from "react";
import { useTranslationHook } from "../../hooks";
import { ExportDefinitionOptions } from "../../types/model/export/ExportDefinitionOptions";
import DebouncedLabelField from "./DebouncedLabelField";

type JoinOption = ExportDefinitionOptions["joins"][number];
type JoinPathHop = JoinOption["path"][number];

interface SelectedColumn {
  column: string;
  label: string;
  table?: string;
  joinKey?: string;
}

interface JoinNode {
  join: JoinOption;
  children: JoinNode[];
}

interface JoinsTreeProps {
  joins: JoinOption[];
  selectedColumns: SelectedColumn[];
  onToggleColumn: (column: string, join: JoinOption) => void;
  onUpdateColumnLabel: (column: string, join: JoinOption, label: string) => void;
  submitting?: boolean;
}

const serializePath = (path: JoinPathHop[]) =>
  path.map((h) => `${h.fromColumn}>${h.toTable}.${h.toColumn}`).join("|");

const columnKey = (column: string, joinKey?: string) => `${joinKey ?? "__base__"}.${column}`;

const pathsEqual = (a: JoinPathHop[], b: JoinPathHop[]) =>
  a.length === b.length &&
  a.every(
    (hop, i) =>
      hop.fromColumn === b[i].fromColumn &&
      hop.toTable === b[i].toTable &&
      hop.toColumn === b[i].toColumn,
  );

const buildJoinTree = (joins: JoinOption[]): JoinNode[] => {
  const nodes: JoinNode[] = joins.map((join) => ({ join, children: [] }));
  const roots: JoinNode[] = [];

  nodes.forEach((node) => {
    const parentPath = node.join.path.slice(0, -1);

    if (parentPath.length === 0) {
      roots.push(node);
      return;
    }

    const parent = nodes.find((n) => pathsEqual(n.join.path, parentPath));

    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const JoinsTree = memo(({
  joins,
  selectedColumns,
  onToggleColumn,
  onUpdateColumnLabel,
  submitting = false,
}: JoinsTreeProps) => {
  const { t } = useTranslationHook();

  const joinTree = useMemo(() => buildJoinTree(joins), [joins]);

  const isColumnSelected = (column: string, join: JoinOption) =>
    selectedColumns.some(
      (c) => columnKey(c.column, c.joinKey) === columnKey(column, serializePath(join.path)),
    );

  const renderJoinNode = (node: JoinNode, depth = 0) => {
    const { join, children } = node;
    const joinKey = serializePath(join.path);
    const selectedCount = selectedColumns.filter((c) => c.joinKey === joinKey).length;
    const lastHop = join.path[join.path.length - 1];

    const viaColumnLabel = t("exports.admin.dialog.via-column").replace(
      "{{column}}",
      lastHop.fromColumn,
    );

    return (
      <Box key={joinKey} sx={{ ml: depth * 3 }}>
        <Accordion disableGutters variant="outlined" sx={{ mb: 1, mt: depth > 0 ? 1 : 0 }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {join.table}
              </Typography>

              <Chip
                label={viaColumnLabel}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  borderColor: "primary.main",
                }}
              />

              {selectedCount > 0 && (
                <Chip label={selectedCount} size="small" color="secondary" sx={{ height: 20 }} />
              )}
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            <Stack spacing={1}>
              {join.columns.map((opt) => {
                const selected = isColumnSelected(opt.column, join);
                const current = selectedColumns.find(
                  (c) => columnKey(c.column, c.joinKey) === columnKey(opt.column, joinKey),
                );

                return (
                  <Stack
                    key={opt.column}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ opacity: submitting ? 0.6 : 1 }}
                  >
                    <FormControlLabel
                      sx={{ flex: 1, minWidth: 0 }}
                      control={
                        <Checkbox
                          checked={selected}
                          disabled={submitting}
                          onChange={() => onToggleColumn(opt.column, join)}
                        />
                      }
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{opt.column}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {opt.dataType}
                          </Typography>
                        </Stack>
                      }
                    />

                    {selected && (
                      <DebouncedLabelField
                        label={t("exports.admin.dialog.column-label")}
                        value={current?.label ?? ""}
                        disabled={submitting}
                        onCommit={(newLabel) => onUpdateColumnLabel(opt.column, join, newLabel)}
                        sx={{ width: 300 }}
                      />
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {children.length > 0 && (
          <Box sx={{ pl: 2, borderLeft: "2px solid", borderColor: "divider" }}>
            {children.map((child) => renderJoinNode(child, depth + 1))}
          </Box>
        )}
      </Box>
    );
  };

  if (joins.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t("generic.no-data")}
      </Typography>
    );
  }

  return <>{joinTree.map((node) => renderJoinNode(node))}</>;
});

export default JoinsTree;